import {
	QueryObserverOptions,
	useQuery,
	useQueryClient,
	useMutation,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { NotesService } from '../../lib/api/notes.services'
import {
	CreateNoteData,
	DefaultNotesParamsProps,
	GetNotesDataResponse,
	GetNoteSharedUsersDataResponse,
	GetNotesParamsProps,
	GetTagsDataResponse,
	PinNoteDataProps,
	SetSharedUsersDataProps,
} from '../../lib/api/types/notes.interfaces'
import { handleApiError } from '../../lib/utils/handleApiError'
import toastCaller from '../../lib/utils/toastCaller'

export const useNotes = (
	{ offset = 0, limit = 10, ...params }: GetNotesParamsProps,
	options?: Partial<ReturnType<typeof useQuery<GetNotesDataResponse>>>,
) => {
	const { isAuthenticated } = useAuth()
	const queryClient = useQueryClient()

	const pageSize = limit * 3
	const forcedOffset = Math.floor(offset / pageSize) * pageSize

	const queryKey = ['notes', limit, JSON.stringify(params), forcedOffset]

	const query = useQuery<GetNotesDataResponse>({
		queryKey,
		queryFn: async () => {
			const fresh = await NotesService.get({
				offset: forcedOffset,
				limit: pageSize,
				...params,
			})

			queryClient.setQueryData(queryKey, fresh)

			const start = offset - fresh.data.pagination.offset
			const end = start + limit

			return {
				...fresh,
				data: {
					...fresh.data,
					entries: fresh?.data?.entries?.slice(start, end),
					pagination: {
						...fresh.data.pagination,
						offset,
						limit,
					},
				},
			}
		},
		enabled: isAuthenticated,
		staleTime: 5000,
		retry: 1,
		placeholderData: () => {
			const cached = queryClient.getQueryData<GetNotesDataResponse>(queryKey)
			if (!cached) return undefined

			const start = offset - cached.data.pagination.offset
			const end = start + limit

			return {
				...cached,
				data: {
					...cached.data,
					entries: cached.data.entries.slice(start, end),
					pagination: {
						...cached.data.pagination,
						offset,
						limit,
					},
				},
			}
		},
		...options,
	})

	return query
}

export const useFileNotes = (
	{ fileId }: { fileId: string },
	options?: Partial<QueryObserverOptions<GetNotesDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetNotesDataResponse>({
		queryKey: ['file-notes', fileId],
		queryFn: () => NotesService.get({ fileId, limit: 100 }),
		enabled: isAuthenticated && Boolean(fileId),
		staleTime: 1000 * 5,
		retry: 1,
		...options,
	})
}

export const useCreateNote = () => {
	const queryClient = useQueryClient()
	const invalidateNotes = () => {
		queryClient.invalidateQueries({ queryKey: ['notes'], exact: false })
		queryClient.invalidateQueries({ queryKey: ['notes-current'], exact: false })
		queryClient.invalidateQueries({ queryKey: ['file-notes'], exact: false })
	}

	const mutation = useMutation({
		mutationKey: ['noteCreate'],
		mutationFn: ({ data }: { data: CreateNoteData }) =>
			NotesService.create({ data }),
		onError: (error, { data }) => handleApiError(error, data?.title),
		onSuccess: () => {
			invalidateNotes()
			toastCaller({ type: 'success', message: 'Note successfully created!' })
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useTags = (
	options?: Partial<QueryObserverOptions<GetTagsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetTagsDataResponse>({
		queryKey: ['tags'],
		queryFn: () => NotesService.getTags(),
		enabled: isAuthenticated,
		staleTime: 1000 * 5,
		retry: 1,
		...options,
	})
}

export const useUpdateNote = () => {
	const queryClient = useQueryClient()
	const invalidateNotes = () => {
		queryClient.invalidateQueries({ queryKey: ['notes'], exact: false })
		queryClient.invalidateQueries({ queryKey: ['notes-current'], exact: false })
		queryClient.invalidateQueries({ queryKey: ['file-notes'], exact: false })
	}

	const mutation = useMutation({
		mutationKey: ['noteUpdate'],
		mutationFn: ({
			data,
			id,
			message,
			withoutNotification,
		}: {
			data: CreateNoteData
			id: string
			message?: string
			withoutNotification?: false
		}) => NotesService.update({ data, id }),
		onError: (error, { data }) => handleApiError(error, data?.title),
		onSuccess: (
			{ data: updatedNote },
			{ message = "Note's updated successfully!", withoutNotification = false },
		) => {
			const notesKeys = queryClient
				.getQueryCache()
				.findAll({ queryKey: ['notes'], exact: false })

			for (const query of notesKeys) {
				const oldData = query.state.data as GetNotesDataResponse | undefined
				if (!oldData) continue

				const newEntries = oldData?.data?.entries.map((entry) =>
					entry?.id === updatedNote?.id ? updatedNote : entry,
				)

				queryClient.setQueryData(query.queryKey, {
					...oldData,
					data: {
						...oldData.data,
						entries: newEntries,
					},
				})
			}

			const currentKeys = queryClient
				.getQueryCache()
				.findAll({ queryKey: ['notes-current'], exact: false })

			for (const query of currentKeys) {
				const oldData = query.state.data as GetNotesDataResponse | undefined
				if (!oldData) continue

				const newEntries = oldData?.data?.entries.map((entry) =>
					entry?.id === updatedNote?.id ? updatedNote : entry,
				)

				queryClient.setQueryData(query.queryKey, {
					...oldData,
					data: {
						...oldData.data,
						entries: newEntries,
					},
				})
			}

			!withoutNotification && toastCaller({ type: 'success', message })
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteNote = () => {
	const queryClient = useQueryClient()
	const invalidateNotes = () => {
		queryClient.invalidateQueries({ queryKey: ['notes'], exact: false })
		queryClient.invalidateQueries({ queryKey: ['notes-current'], exact: false })
		queryClient.invalidateQueries({ queryKey: ['file-notes'], exact: false })
	}

	const mutation = useMutation({
		mutationKey: ['groupDelete'],
		mutationFn: ({ id }: DefaultNotesParamsProps) =>
			NotesService.delete({ id }),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: () => invalidateNotes(),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const usePinNote = () => {
	const queryClient = useQueryClient()

	const invalidateNotes = () => {
		queryClient.invalidateQueries({ queryKey: ['notes'], exact: false })
		queryClient.invalidateQueries({ queryKey: ['notes-current'], exact: false })
		queryClient.invalidateQueries({ queryKey: ['file-notes'], exact: false })
	}

	const mutation = useMutation({
		mutationKey: ['notePin'],
		mutationFn: ({ data }: { data: PinNoteDataProps }) =>
			NotesService.pin(data),
		onError: (error) => handleApiError(error),
		onSuccess: () => invalidateNotes(),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useNoteSharedUsers = (
	params: DefaultNotesParamsProps,
	options?: Partial<QueryObserverOptions<GetNoteSharedUsersDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetNoteSharedUsersDataResponse>({
		queryKey: ['noteSharedUsers', params?.id],
		queryFn: () => NotesService.getNoteSharedUsers(params),
		enabled: isAuthenticated && Boolean(params?.id),
		staleTime: 1000 * 60 * 5,
		...options,
	})
}

export const useUpdateNoteSharedUsers = () => {
	const queryClient = useQueryClient()

	const invalidateNotes = () => {
		queryClient.invalidateQueries({ queryKey: ['notes'], exact: false })
		queryClient.invalidateQueries({ queryKey: ['notes-current'], exact: false })
		queryClient.invalidateQueries({ queryKey: ['file-notes'], exact: false })
		queryClient.invalidateQueries({
			queryKey: ['noteSharedUsers'],
			exact: false,
		})
	}

	const mutation = useMutation({
		mutationKey: ['noteUpdateSharedUsers'],
		mutationFn: (data: SetSharedUsersDataProps) =>
			NotesService.setNoteSharedUsers(data),
		onError: (error) => handleApiError(error),
		onSuccess: () => invalidateNotes(),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
